const CACHE_VERSION = 2;
const CACHE_PREFIX = "clipr:schedule-clip-library:";
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

function normalizeDays(days) {
  if (days === undefined || days === null || days === "") return "all";
  return String(days);
}

export function buildScheduleClipLibraryCacheKey({
  userId,
  days,
  ownClipsOnly = false,
  assignedAdminOnly = false,
  showEarlyJobStatuses = false,
} = {}) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return "";
  return [
    CACHE_PREFIX,
    `v${CACHE_VERSION}`,
    normalizedUserId,
    normalizeDays(days),
    ownClipsOnly ? "own" : "all-users",
    assignedAdminOnly ? "assigned" : "any-assignment",
    showEarlyJobStatuses ? "early" : "ready",
  ].join(":");
}

export function readScheduleClipLibraryCache(params) {
  const cacheKey = buildScheduleClipLibraryCacheKey(params);
  if (!cacheKey || typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CACHE_VERSION || !parsed.payload) return null;
    if (!Number.isFinite(parsed.savedAt) || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.payload;
  } catch (_error) {
    return null;
  }
}

export function writeScheduleClipLibraryCache(params, payload) {
  const cacheKey = buildScheduleClipLibraryCacheKey(params);
  if (!cacheKey || typeof window === "undefined" || !payload) return;

  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify({
      version: CACHE_VERSION,
      savedAt: Date.now(),
      payload,
    }));
  } catch (_error) {
    // Ignore quota or serialization failures.
  }
}

export function invalidateScheduleClipLibraryCache(params) {
  const cacheKey = buildScheduleClipLibraryCacheKey(params);
  if (!cacheKey || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(cacheKey);
  } catch (_error) {
    // Ignore unavailable session storage.
  }
}

export function invalidateAllScheduleClipLibraryCaches() {
  if (typeof window === "undefined") return;
  try {
    const keys = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key && key.startsWith(CACHE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_error) {
    // Ignore unavailable session storage.
  }
}

export function didScheduleClipLibraryMediaRevisionChange(previousJob, nextJob) {
  if (!previousJob || !nextJob) return false;
  return (
    previousJob.media_revision !== nextJob.media_revision
    || previousJob.media_storage_state !== nextJob.media_storage_state
    || previousJob.media_tombstoned_at !== nextJob.media_tombstoned_at
    || previousJob.job_thumbnail_url !== nextJob.job_thumbnail_url
    || previousJob.updated_at !== nextJob.updated_at
  );
}

export function didScheduleClipMediaRevisionChange(previousClip, nextClip) {
  if (!previousClip || !nextClip) return false;
  return (
    previousClip.media_revision !== nextClip.media_revision
    || previousClip.media_storage_state !== nextClip.media_storage_state
    || previousClip.media_tombstoned_at !== nextClip.media_tombstoned_at
    || previousClip.rendered_video_url !== nextClip.rendered_video_url
    || previousClip.thumbnail_url !== nextClip.thumbnail_url
    || previousClip.updated_at !== nextClip.updated_at
  );
}
